import { Stack } from "@chakra-ui/react";
import { FC, useEffect } from "react";
import {
  Control,
  FieldValues,
  UseFormResetField,
  UseFormWatch,
} from "react-hook-form";

import InformationDataForm from "./InformationDataForm";
import IndicatorReferencesForm from "./IndicatorReferencesForm";
import ReadingIndicatorVariationsForm from "./ReadingIndicatorVariationsForm";
import ClassificationIndicatorForm from "./ClassificationIndicatorForm";
import AlertStateForm from "./AlertStateForm";
import { useSkipFirstRender } from "../../../../hooks/useSkipFirstRender";

interface UserSubmitFormProps {
  errors: any;
  control: Control<any>;
  isDisabled?: boolean;
  watch: UseFormWatch<FieldValues>;
  resetField: UseFormResetField<FieldValues>;
  isFormInitialized?: boolean;
  currentStep?: number;
  setError: (name: any, error: any) => void;
  clearErrors: (name?: any) => void;
  setHasDuplicateNameError: (hasError: boolean) => void; // ✅ NOVO
  currentId?: number; // ✅ NOVO
}

const IndicatorSubmitForm: FC<UserSubmitFormProps> = ({
  errors,
  control,
  isDisabled,
  watch,
  resetField,
  isFormInitialized = true,
  currentStep,
  setError,
  clearErrors,
  setHasDuplicateNameError,
  currentId, // ✅ NOVO
}) => {
  const selectedIndicatorMetric = isFormInitialized
    ? watch("indicatorMetricOption")
    : "";
  const selectedOutra = isFormInitialized ? watch("outra") : "";
  const selectedHealthyLevel = isFormInitialized
    ? watch("healthyLevelOption")
    : "";
  const selectedUnhealthyLevel = isFormInitialized
    ? watch("unhealthyLevelOption")
    : "";
  const selectedAlertVariationCheck = isFormInitialized
    ? watch("alertVariationCheck")
    : "";

  useEffect(() => {
    if (selectedIndicatorMetric && selectedIndicatorMetric.id !== 4) {
      resetField("outra");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedIndicatorMetric]);

  useEffect(() => {
    if (!selectedAlertVariationCheck) {
      resetField("superiorAbsoluteValue");
      resetField("minPeriodVariation");
      resetField("maxPeriodVariation");
      resetField("inferiorYearlyLevelVariations");
      resetField("superiorYearlyLevelVariations");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAlertVariationCheck]);

  const skipFirstHealthyRender = useSkipFirstRender();

  useEffect(() => {
    if (!isFormInitialized) return;
    if (skipFirstHealthyRender()) return;

    if (!currentStep || currentStep === 0) {
      resetField("healthyIntervalFrom");
      resetField("healthyIntervalTo");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedHealthyLevel]);

  const skipFirstUnhealthyRender = useSkipFirstRender();

  useEffect(() => {
    if (!isFormInitialized) return;
    if (skipFirstUnhealthyRender()) return;

    if (!currentStep || currentStep === 0) {
      resetField("unhealthyIntervalFrom");
      resetField("unhealthyIntervalTo");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedUnhealthyLevel]);

  return (
    <Stack spacing={8}>
      <InformationDataForm
        errors={errors}
        control={control}
        watch={watch}
        isDisabled={isDisabled}
        selectedIndicatorMetric={selectedIndicatorMetric}
        setError={setError}
        clearErrors={clearErrors}
        setHasDuplicateNameError={setHasDuplicateNameError}
        currentId={currentId}
      />

      <IndicatorReferencesForm
        errors={errors}
        control={control}
        watch={watch}
        selectedIndicatorMetric={
          selectedIndicatorMetric
            ? selectedIndicatorMetric.id === 4
              ? selectedOutra
              : selectedIndicatorMetric.name
            : undefined
        }
        selectedHealthyLevel={
          selectedHealthyLevel && selectedHealthyLevel.id.toString()
        }
        selectedUnhealthyLevel={
          selectedUnhealthyLevel && selectedUnhealthyLevel.id.toString()
        }
        isDisabled={isDisabled}
      />

      <ReadingIndicatorVariationsForm
        errors={errors}
        control={control}
        watch={watch}
        isDisabled={isDisabled}
      />

      <ClassificationIndicatorForm
        errors={errors}
        control={control}
        watch={watch}
        isDisabled={isDisabled}
      />

      <AlertStateForm
        errors={errors}
        control={control}
        watch={watch}
        isDisabled={isDisabled}
        selectedIndicatorMetric={
          selectedIndicatorMetric
            ? selectedIndicatorMetric.id === 4
              ? selectedOutra
              : selectedIndicatorMetric.name
            : undefined
        }
      />
    </Stack>
  );
};

export default IndicatorSubmitForm;
